import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FunctionItem2D } from '../../types/math';
import { ZoomIn, ZoomOut, RotateCcw, Crosshair } from 'lucide-react';

interface Plot2DProps {
  item: FunctionItem2D;
  customParams?: Record<string, number>;
  showAsymptotes?: boolean;
  interactive?: boolean;
  className?: string;
}

export const Plot2D: React.FC<Plot2DProps> = ({
  item,
  customParams,
  showAsymptotes = true,
  interactive = true,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Viewport bounds in mathematical coordinates
  const [bounds, setBounds] = useState<{ xMin: number; xMax: number; yMin: number; yMax: number }>({
    xMin: item.xDomain[0],
    xMax: item.xDomain[1],
    yMin: item.yDomain[0],
    yMax: item.yDomain[1]
  });

  // Mouse hover coordinate tracking
  const [hoverCoord, setHoverCoord] = useState<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Reset bounds whenever item changes
  useEffect(() => {
    setBounds({
      xMin: item.xDomain[0],
      xMax: item.xDomain[1],
      yMin: item.yDomain[0],
      yMax: item.yDomain[1]
    });
    setHoverCoord(null);
  }, [item.id, item.xDomain, item.yDomain]);

  // Coordinate transforms
  const toScreen = useCallback((x: number, y: number, width: number, height: number) => {
    const screenX = ((x - bounds.xMin) / (bounds.xMax - bounds.xMin)) * width;
    const screenY = height - ((y - bounds.yMin) / (bounds.yMax - bounds.yMin)) * height;
    return { screenX, screenY };
  }, [bounds]);

  const toMath = useCallback((screenX: number, screenY: number, width: number, height: number) => {
    const x = bounds.xMin + (screenX / width) * (bounds.xMax - bounds.xMin);
    const y = bounds.yMax - (screenY / height) * (bounds.yMax - bounds.yMin);
    return { x, y };
  }, [bounds]);

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // Canvas size in actual physical pixels
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    ctx.save();
    ctx.scale(dpr, dpr);

    // 1. Background
    ctx.fillStyle = '#070d19';
    ctx.fillRect(0, 0, width, height);

    // 2. Grid Lines & Ticks
    const xSpan = bounds.xMax - bounds.xMin;
    const ySpan = bounds.yMax - bounds.yMin;

    // Calculate nice step sizes for ticks
    const getNiceStep = (span: number) => {
      const rawStep = span / 8;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
      const residual = rawStep / magnitude;
      if (residual > 5) return 5 * magnitude;
      if (residual > 2) return 2 * magnitude;
      return magnitude;
    };

    const xStep = getNiceStep(xSpan);
    const yStep = getNiceStep(ySpan);

    // Minor grid
    ctx.strokeStyle = '#121d33';
    ctx.lineWidth = 1;
    ctx.beginPath();

    const startX = Math.floor(bounds.xMin / xStep) * xStep;
    for (let x = startX; x <= bounds.xMax; x += xStep) {
      const { screenX } = toScreen(x, 0, width, height);
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, height);
    }

    const startY = Math.floor(bounds.yMin / yStep) * yStep;
    for (let y = startY; y <= bounds.yMax; y += yStep) {
      const { screenY } = toScreen(0, y, width, height);
      ctx.moveTo(0, screenY);
      ctx.lineTo(width, screenY);
    }
    ctx.stroke();

    // 3. Axes
    const origin = toScreen(0, 0, width, height);
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 1.5;
    ctx.beginPath();

    // X axis
    if (origin.screenY >= 0 && origin.screenY <= height) {
      ctx.moveTo(0, origin.screenY);
      ctx.lineTo(width, origin.screenY);
    }
    // Y axis
    if (origin.screenX >= 0 && origin.screenX <= width) {
      ctx.moveTo(origin.screenX, 0);
      ctx.lineTo(origin.screenX, height);
    }
    ctx.stroke();

    // Axis Labels and numbers
    ctx.fillStyle = '#64748b';
    ctx.font = '11px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // Numbers along X axis
    const axisYPos = Math.min(Math.max(origin.screenY + 4, 15), height - 18);
    for (let x = startX; x <= bounds.xMax; x += xStep) {
      if (Math.abs(x) < 0.0001) continue; // skip 0
      const { screenX } = toScreen(x, 0, width, height);
      if (screenX > 25 && screenX < width - 25) {
        ctx.fillText(Number(x.toFixed(2)).toString(), screenX, axisYPos);
      }
    }

    // Numbers along Y axis
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const axisXPos = Math.min(Math.max(origin.screenX - 6, 28), width - 8);
    for (let y = startY; y <= bounds.yMax; y += yStep) {
      if (Math.abs(y) < 0.0001) continue;
      const { screenY } = toScreen(0, y, width, height);
      if (screenY > 15 && screenY < height - 15) {
        ctx.fillText(Number(y.toFixed(2)).toString(), axisXPos, screenY);
      }
    }

    // 4. Asymptotes (if enabled)
    if (showAsymptotes && item.asymptotes) {
      ctx.save();
      ctx.strokeStyle = '#f43f5e';
      ctx.lineWidth = 1.2;
      ctx.setLineDash([5, 5]);

      // Vertical asymptotes
      item.asymptotes.vertical?.forEach((vx) => {
        const { screenX } = toScreen(vx, 0, width, height);
        ctx.beginPath();
        ctx.moveTo(screenX, 0);
        ctx.lineTo(screenX, height);
        ctx.stroke();
      });

      // Horizontal asymptotes
      item.asymptotes.horizontal?.forEach((hy) => {
        const { screenY } = toScreen(0, hy, width, height);
        ctx.beginPath();
        ctx.moveTo(0, screenY);
        ctx.lineTo(width, screenY);
        ctx.stroke();
      });
      ctx.restore();
    }

    // 5. Function Curve Plotting
    ctx.save();
    // Glowing neon teal line
    ctx.strokeStyle = '#2dd4bf';
    ctx.lineWidth = 3;
    ctx.shadowColor = '#14b8a6';
    ctx.shadowBlur = 8;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const numSamples = Math.floor(width * 1.5);
    let isDrawingSegment = false;
    let prevScreenY: number | null = null;

    ctx.beginPath();
    for (let i = 0; i <= numSamples; i++) {
      const screenX = (i / numSamples) * width;
      const mathX = bounds.xMin + (screenX / width) * xSpan;
      let mathY = item.evaluate(mathX, customParams);

      if (isNaN(mathY) || !isFinite(mathY) || Math.abs(mathY) > 1e4) {
        isDrawingSegment = false;
        prevScreenY = null;
        continue;
      }

      const { screenY } = toScreen(mathX, mathY, width, height);

      // Check for sudden vertical asymptotic leaps (e.g. tan(x), 1/x)
      if (prevScreenY !== null && Math.abs(screenY - prevScreenY) > height * 0.75) {
        isDrawingSegment = false;
        prevScreenY = null;
        continue;
      }

      if (!isDrawingSegment) {
        ctx.moveTo(screenX, screenY);
        isDrawingSegment = true;
      } else {
        ctx.lineTo(screenX, screenY);
      }
      prevScreenY = screenY;
    }
    ctx.stroke();
    ctx.restore();

    // 6. Crosshair & Point Tracker
    if (hoverCoord) {
      const { screenX, screenY } = toScreen(hoverCoord.x, hoverCoord.y, width, height);
      ctx.save();
      ctx.strokeStyle = '#94a3b8';
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;

      // Crosshair lines
      ctx.beginPath();
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, height);
      ctx.moveTo(0, screenY);
      ctx.lineTo(width, screenY);
      ctx.stroke();

      // Point circle
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(screenX, screenY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.stroke();

      // Tooltip badge
      const text = `(${hoverCoord.x.toFixed(2)}, ${hoverCoord.y.toFixed(2)})`;
      ctx.font = '12px "JetBrains Mono", monospace';
      const textWidth = ctx.measureText(text).width;
      const badgeX = Math.min(Math.max(screenX + 12, 10), width - textWidth - 20);
      const badgeY = Math.min(Math.max(screenY - 24, 15), height - 20);

      ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(badgeX - 6, badgeY - 14, textWidth + 12, 22, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#38bdf8';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, badgeX, badgeY - 3);

      ctx.restore();
    }

    ctx.restore();
  }, [bounds, item, customParams, showAsymptotes, hoverCoord, toScreen]);

  // Pan interaction
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive) return;
    isDraggingRef.current = true;
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const clientY = e.clientY - rect.top;

    if (isDraggingRef.current && interactive) {
      const dx = e.clientX - lastMousePosRef.current.x;
      const dy = e.clientY - lastMousePosRef.current.y;
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };

      const xSpan = bounds.xMax - bounds.xMin;
      const ySpan = bounds.yMax - bounds.yMin;

      const dMathX = (dx / rect.width) * xSpan;
      const dMathY = (dy / rect.height) * ySpan;

      setBounds((prev) => ({
        xMin: prev.xMin - dMathX,
        xMax: prev.xMax - dMathX,
        yMin: prev.yMin + dMathY,
        yMax: prev.yMax + dMathY
      }));
    } else {
      // Hover coordinate calculation
      const math = toMath(clientX, clientY, rect.width, rect.height);
      const exactY = item.evaluate(math.x, customParams);
      if (!isNaN(exactY) && isFinite(exactY) && Math.abs(exactY) < 1000) {
        setHoverCoord({ x: math.x, y: exactY });
      } else {
        setHoverCoord(null);
      }
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    if (!interactive) return;
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 0.9 : 1.1;
    zoom(zoomFactor);
  };

  const zoom = (factor: number) => {
    setBounds((prev) => {
      const xCenter = (prev.xMin + prev.xMax) / 2;
      const yCenter = (prev.yMin + prev.yMax) / 2;
      const halfX = ((prev.xMax - prev.xMin) * factor) / 2;
      const halfY = ((prev.yMax - prev.yMin) * factor) / 2;
      return {
        xMin: xCenter - halfX,
        xMax: xCenter + halfX,
        yMin: yCenter - halfY,
        yMax: yCenter + halfY
      };
    });
  };

  const resetView = () => {
    setBounds({
      xMin: item.xDomain[0],
      xMax: item.xDomain[1],
      yMin: item.yDomain[0],
      yMax: item.yDomain[1]
    });
  };

  return (
    <div className={`relative w-full h-full flex flex-col rounded-xl overflow-hidden border border-slate-800 bg-[#070d19] ${className}`}>
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          isDraggingRef.current = false;
          setHoverCoord(null);
        }}
        onWheel={handleWheel}
        className="w-full h-full cursor-crosshair touch-none"
      />

      {/* Floating Controls Overlay */}
      {interactive && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-slate-900/80 backdrop-blur-md px-2 py-1.5 rounded-lg border border-slate-700/60 shadow-lg text-slate-300">
          <button
            onClick={() => zoom(0.8)}
            title="Zoom In"
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => zoom(1.25)}
            title="Zoom Out"
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={resetView}
            title="Reset View"
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <div className="w-[1px] h-4 bg-slate-700 mx-0.5" />
          <span className="text-[10px] font-mono text-slate-400 px-1 flex items-center gap-1">
            <Crosshair className="w-3 h-3 text-teal-400" /> 2D
          </span>
        </div>
      )}
    </div>
  );
};
